import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteComponentProps } from "react-router";
import * as Sentry from "@sentry/browser";
import "../../styles/pages/ProductStatements.css";
import { Drawer } from "antd";
import { NotificationOutlined } from "@ant-design/icons";
import Icon from "./Icon";
import OverviewTab from "./overview/OverviewTab";
import RowDetailDrawer from "./overview/RowDetailDrawer";
import UploadsTab from "./uploads/UploadsTab";
import AuditDrawer from "./uploads/AuditDrawer";
import ReasonModal from "./uploads/ReasonModal";
import NotificationsView from "./notifications/NotificationsView";
import { ToastProvider, useToast } from "./Toast";
import { formatFriendly } from "./helpers";
import {
  ApiAuditEvent,
  ApiStatementUpload,
  AuditEvent,
  ValidationError,
  ReconRow,
  Tweaks,
  FormattedUpload,
  UploadListQuery,
  UploadStatementPayload,
} from "./types";
import { toValidationError, formatUploadPayload } from "./apiMapper";
import { useUploadPolling } from "./useUploadPolling";
import { request } from "../../helpers/utilities";
import {
  CANCEL_STATEMENT_UPLOAD,
  DOWNLOAD_STATEMENT_UPLOAD_BUNDLE,
  EXPEDITE_STATEMENT_UPLOAD,
  GET_STATEMENT_UPLOAD,
  GET_STATEMENT_UPLOAD_AUDIT_EVENTS,
  GET_STATEMENT_UPLOADS,
  REPLACE_STATEMENT_UPLOAD_FILE,
  RESCHEDULE_STATEMENT_UPLOAD,
  RETRY_STATEMENT_UPLOAD,
  REVERSE_STATEMENT_UPLOAD,
  UPLOAD_FILE,
  UPLOAD_STATEMENT,
} from "../../gql/productStatements";
import { GET_CURRENT_USER } from "../../gql/users";

const ACTION_TITLES: Record<
  string,
  { title: string; kind: AuditEvent["kind"] }
> = {
  file_uploaded: { title: "File uploaded", kind: "success" },
  reconciliation_scheduled: { title: "Reconciliation scheduled", kind: "info" },
  file_replaced: { title: "File replaced", kind: "warn" },
  rescheduled: { title: "Run date moved earlier", kind: "info" },
  expedited: { title: "Started immediately", kind: "warn" },
  awaiting_prices: { title: "Waiting on unit prices", kind: "warn" },
  scheduled_canceled: { title: "Canceled", kind: "fail" },
  file_looks_good: { title: "File looks good", kind: "success" },
  statements_ready: { title: "Statements ready", kind: "success" },
  finished_with_issues: { title: "Finished with issues", kind: "warn" },
  file_rejected: { title: "File failed checks", kind: "fail" },
  parse_recovered: { title: "Checked after a delay", kind: "warn" },
  reconciliation_failed: { title: "Reconciliation failed", kind: "fail" },
  reverse_requested: { title: "Reverse requested", kind: "warn" },
  reversing: { title: "Reversing", kind: "warn" },
  reversed: { title: "Reversed", kind: "fail" },
};

function fmtFriendlyDate(iso: string | null | undefined): string {
  return formatFriendly(iso, true);
}

function describeEvent(
  action: string,
  details: Record<string, unknown> | null,
  fundCodeToName: Record<string, string> = {},
): string {
  if (!details) return "";
  const str = (key: string): string =>
    typeof details[key] === "string" ? (details[key] as string) : "";
  const num = (key: string): number | null =>
    typeof details[key] === "number" ? (details[key] as number) : null;
  const arr = (key: string): string[] =>
    Array.isArray(details[key]) ? (details[key] as string[]) : [];

  switch (action) {
    case "file_uploaded": {
      const sheetCount = num("sheet_count");
      const fundCodes = arr("fund_codes");
      const parts: string[] = [];
      if (sheetCount != null) {
        parts.push(`${sheetCount} product${sheetCount === 1 ? "" : "s"} found`);
      }
      if (fundCodes.length) {
        // Prefer the product name when we know it; fall back to the raw fund
        // code so older events without a name lookup still read sensibly.
        const labeled = fundCodes.map((code) => fundCodeToName[code] || code);
        parts.push(labeled.join(", "));
      }
      return parts.join(" · ");
    }
    case "reconciliation_scheduled": {
      const when = fmtFriendlyDate(str("scheduled_for"));
      const reason = str("rationale");
      return [when && `Runs on ${when}`, reason].filter(Boolean).join(" · ");
    }
    case "file_replaced": {
      const oldF = str("old_filename");
      const newF = str("new_filename");
      if (oldF && newF) return `${oldF} → ${newF}`;
      return newF || oldF;
    }
    case "rescheduled": {
      const from = fmtFriendlyDate(str("from"));
      const to = fmtFriendlyDate(str("to"));
      const reason = str("reason");
      return [
        from && to ? `${from} → ${to}` : to || from,
        reason && `reason: ${reason}`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "scheduled_canceled":
    case "reverse_requested": {
      const reason = str("reason");
      return reason ? `Reason: ${reason}` : "";
    }
    case "expedited": {
      const from = fmtFriendlyDate(str("from"));
      const reason = str("reason");
      return [
        from && `Was scheduled for ${from}`,
        reason && `Reason: ${reason}`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    case "awaiting_prices": {
      const pairs = Array.isArray(details.pairs)
        ? (details.pairs as Array<{ fund_code?: string; value_date?: string }>)
        : [];
      if (!pairs.length) return "";
      const labels = pairs.map(
        (pair) =>
          `${pair.fund_code || "?"} (${fmtFriendlyDate(pair.value_date)})`,
      );
      return `Missing: ${labels.join(", ")}`;
    }
    case "statements_ready": {
      const processed = num("processed");
      const seconds = num("duration_s");
      const parts: string[] = [];
      if (processed != null) parts.push(`${processed.toLocaleString()} rows`);
      if (seconds != null) parts.push(`took ${seconds}s`);
      return parts.join(" · ");
    }
    case "finished_with_issues": {
      const processed = num("processed");
      const failed = num("failed");
      const err = str("error_summary");
      const parts: string[] = [];
      if (processed != null)
        parts.push(`${processed.toLocaleString()} reconciled`);
      if (failed != null)
        parts.push(`${failed} issue${failed === 1 ? "" : "s"}`);
      if (err) parts.push(err);
      return parts.join(" · ");
    }
    case "file_rejected": {
      const count = num("error_count");
      const codes = arr("codes");
      const parts: string[] = [];
      if (count != null)
        parts.push(`${count} problem${count === 1 ? "" : "s"} found`);
      if (codes && codes.length > 0)
        parts.push(codes.map((code) => code.replace(/_/g, " ")).join(", "));
      return parts.join(" · ");
    }
    case "parse_recovered":
      return "The file was checked again after its first attempt did not finish.";
    case "reconciliation_failed": {
      return str("error_summary") || "";
    }
    case "file_looks_good":
    case "reversing":
    case "reversed":
      return "";
    default: {
      // Fallback: render as "key: value · key: value" so users never see raw {}.
      return Object.entries(details)
        .map(
          ([key, value]) =>
            `${key.replace(/_/g, " ")}: ${
              Array.isArray(value) ? value.join(", ") : String(value)
            }`,
        )
        .join(" · ");
    }
  }
}

function apiEventToLegacy(
  event: ApiAuditEvent,
  fundCodeToName: Record<string, string> = {},
): AuditEvent {
  const meta = ACTION_TITLES[event.action] || {
    title: event.action,
    kind: "info" as const,
  };
  const actor = event.actor
    ? {
        name: event.actor.name,
        initials: event.actor.name
          .split(/\s+/)
          .filter(Boolean)
          .map((part) => part[0]?.toUpperCase() || "")
          .slice(0, 2)
          .join(""),
      }
    : { name: "System", initials: "S" };
  return {
    kind: meta.kind,
    title: meta.title,
    desc: describeEvent(event.action, event.details, fundCodeToName),
    time: fmtFriendlyDate(event.createdAt),
    actor,
  };
}

type TabKey = "overview" | "uploads";

const DEFAULT_TWEAKS: Tweaks = { role: "editor" };

function tabFromQuery(search: string): TabKey {
  const params = new URLSearchParams(search);
  return params.get("tab") === "uploads" ? "uploads" : "overview";
}

type Props = RouteComponentProps<Record<string, never>>;

type StatementUploadsResponse = {
  data: {
    data: {
      statementUploads: { results: ApiStatementUpload[]; count: number };
    };
  };
};

const FIRST_UPLOADS_PAGE: UploadListQuery = {
  page: 1,
  pageSize: 10,
  status: null,
  search: "",
};

function uploadsVariables(listQuery: UploadListQuery) {
  return {
    page: listQuery.page,
    pageSize: listQuery.pageSize,
    q: listQuery.search.trim() || null,
    filters: listQuery.status ? { statuses: [listQuery.status] } : null,
  };
}

const ProductStatementsInner: React.FC<Props> = (props) => {
  const [records, setRecords] = useState<FormattedUpload[]>([]);
  const [totalUploads, setTotalUploads] = useState(0);
  const [listQuery, setListQuery] =
    useState<UploadListQuery>(FIRST_UPLOADS_PAGE);
  const latestListRequest = useRef(0);
  const [drawerRec, setDrawerRec] = useState<FormattedUpload | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEvents, setDrawerEvents] = useState<AuditEvent[] | undefined>(
    undefined,
  );
  const [drawerIssues, setDrawerIssues] = useState<
    FormattedUpload["issues"] | undefined
  >(undefined);
  const [rowDrawerRow, setRowDrawerRow] = useState<ReconRow | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<ValidationError | null>(null);
  const [tab, setTab] = useState<TabKey>(tabFromQuery(props.location.search));
  const [tweaks] = useState<Tweaks>(DEFAULT_TWEAKS);
  const [canExpedite, setCanExpedite] = useState(false);
  const [canReschedule, setCanReschedule] = useState(false);
  const [canReverse, setCanReverse] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsMaximized, setNotificationsMaximized] = useState(false);
  const toast = useToast();

  // Reason-modal driver. We render exactly one ReasonModal at the page root
  // and reuse it for any handler that needs a "why?" — reschedule, cancel,
  // reverse. Each handler pushes its title/description and an onConfirm
  // callback in here; the modal calls back with the trimmed reason.
  const [reasonPrompt, setReasonPrompt] = useState<{
    title: string;
    description?: string;
    okText: string;
    okDanger?: boolean;
    onConfirm: (reason: string) => void;
  } | null>(null);
  const askForReason = useCallback(
    (cfg: {
      title: string;
      description?: string;
      okText: string;
      okDanger?: boolean;
      onConfirm: (reason: string) => void;
    }) => setReasonPrompt(cfg),
    [],
  );

  // When the drawer opens for a live record (with dbId), fetch real audit events.
  // We rebuild the fund_code → product_name lookup from the record's sheets
  // so the "File uploaded" event can read product names, not raw codes.
  const drawerDbId = drawerRec?.dbId;
  const fundCodeToName: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};
    (drawerRec?.products || []).forEach((product) => {
      if (product.fundCode) map[product.fundCode] = product.name;
    });
    return map;
  }, [drawerRec]);
  useEffect(() => {
    if (!drawerOpen || !drawerDbId) {
      setDrawerEvents(undefined);
      setDrawerIssues(undefined);
      return;
    }
    Promise.all([
      request({
        query: GET_STATEMENT_UPLOAD_AUDIT_EVENTS,
        variables: { id: drawerDbId },
      }),
      request({
        query: GET_STATEMENT_UPLOAD,
        variables: { id: drawerDbId },
      }),
    ])
      .then(([eventsResponse, uploadResponse]: [any, any]) => {
        setDrawerEvents(
          eventsResponse.data.data.statementUploadAuditEvents.map(
            (event: ApiAuditEvent) => apiEventToLegacy(event, fundCodeToName),
          ),
        );
        const apiUpload = uploadResponse.data.data.statementUpload;
        const formattedUpload = formatUploadPayload(apiUpload);
        setDrawerIssues(formattedUpload.issues);
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
        setDrawerEvents(undefined);
        setDrawerIssues(undefined);
      });
  }, [drawerOpen, drawerDbId, fundCodeToName]);

  const loadRecords = useCallback(() => {
    const requestId = latestListRequest.current + 1;
    latestListRequest.current = requestId;
    setRefreshing(true);
    request({
      query: GET_STATEMENT_UPLOADS,
      variables: uploadsVariables(listQuery),
    })
      .then((response: StatementUploadsResponse) => {
        if (requestId !== latestListRequest.current) return;
        const results = response.data.data.statementUploads?.results ?? [];
        const count = response.data.data.statementUploads?.count ?? 0;
        const lastPage = Math.max(1, Math.ceil(count / listQuery.pageSize));
        if (results.length === 0 && listQuery.page > lastPage) {
          setListQuery((current) => ({ ...current, page: lastPage }));
          return;
        }
        setRecords(results.map(formatUploadPayload));
        setTotalUploads(count);
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        if (requestId === latestListRequest.current) setRefreshing(false);
      });
  }, [listQuery]);
  const loadRecordsRef = useRef(loadRecords);
  loadRecordsRef.current = loadRecords;

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useUploadPolling(records, loadRecords);

  useEffect(() => {
    request({ query: GET_CURRENT_USER })
      .then(
        (
          response:
            | {
                data: {
                  data: {
                    currentUser: {
                      email: string;
                      name: string;
                      role: { name: string };
                      entitlements: Array<{ name: string }>;
                    } | null;
                  };
                };
              }
            | undefined,
        ) => {
          const user = response?.data?.data?.currentUser;
          if (!user) {
            console.warn(
              "[ProductStatements] currentUser query returned no user.",
            );
            return;
          }
          const entitlementNames = (user.entitlements || [])
            .filter((entitlement) =>
              entitlement.name?.startsWith("ProductStatements::"),
            )
            .map((entitlement) => entitlement.name);
          setCanExpedite(
            entitlementNames.includes("ProductStatements::Schedule::Expedite"),
          );
          setCanReschedule(
            entitlementNames.includes("ProductStatements::Reschedule"),
          );
          setCanReverse(
            entitlementNames.includes("ProductStatements::Reverse"),
          );
          // eslint-disable-next-line no-console
          console.info(
            `[ProductStatements] Authenticated as ${user.email} (role: ${
              user.role?.name
            }). ProductStatements entitlements: ${
              entitlementNames.length ? entitlementNames.join(", ") : "NONE"
            }.`,
          );
        },
      )
      .catch((error: unknown) => {
        Sentry.captureException(error);
      });
  }, []);

  useEffect(() => {
    if (!drawerRec) return;
    const updated = records.find((record) => record.id === drawerRec.id);
    if (updated && updated !== drawerRec) setDrawerRec(updated);
  }, [records, drawerRec]);

  const setTabAndUrl = useCallback(
    (next: TabKey) => {
      setTab(next);
      const params = new URLSearchParams(props.location.search);
      if (next === "overview") params.delete("tab");
      else params.set("tab", next);
      const qs = params.toString();
      props.history.replace({
        pathname: props.location.pathname,
        search: qs ? `?${qs}` : "",
      });
    },
    [props.history, props.location.pathname, props.location.search],
  );

  const openDrawer = useCallback((record: FormattedUpload) => {
    setDrawerRec(record);
    setDrawerOpen(true);
  }, []);
  const findUpload = useCallback(
    (publicId: string): Promise<FormattedUpload | undefined> => {
      const loaded = records.find((upload) => upload.id === publicId);
      if (loaded) return Promise.resolve(loaded);
      return request({
        query: GET_STATEMENT_UPLOADS,
        variables: { q: publicId, page: 1, pageSize: 5 },
      }).then((response: StatementUploadsResponse) =>
        (response.data.data.statementUploads?.results ?? [])
          .map(formatUploadPayload)
          .find((upload) => upload.id === publicId),
      );
    },
    [records],
  );
  const openDrawerById = useCallback(
    (publicId: string) => {
      findUpload(publicId)
        .then((record) => {
          if (!record) return;
          setDrawerRec(record);
          setDrawerOpen(true);
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
        });
    },
    [findUpload],
  );
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    const params = new URLSearchParams(props.location.search);
    if (params.has("upload")) {
      params.delete("upload");
      const qs = params.toString();
      props.history.replace({
        pathname: props.location.pathname,
        search: qs ? `?${qs}` : "",
      });
    }
  }, [props.history, props.location.pathname, props.location.search]);

  const linkedUploadId = useRef<string | null>(null);
  useEffect(() => {
    const uploadId = new URLSearchParams(props.location.search).get("upload");
    if (!uploadId) {
      linkedUploadId.current = null;
      return;
    }
    if (records.length === 0 || linkedUploadId.current === uploadId) return;

    linkedUploadId.current = uploadId;
    findUpload(uploadId)
      .then((record) => {
        if (!record || linkedUploadId.current !== uploadId) return;
        setDrawerRec(record);
        setDrawerOpen(true);
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
      });
  }, [props.location.search, records, findUpload]);

  const handleUpload = useCallback(
    ({
      file,
      isInceptionToDate,
    }: {
      file: File;
      isInceptionToDate: boolean;
    }): void => {
      setUploading(true);
      setUploadError(null);

      const reader = new FileReader();
      reader.onload = (): void => {
        request({
          query: UPLOAD_STATEMENT,
          variables: {
            filename: file.name,
            base64Contents: String(reader.result || ""),
            byteSize: file.size,
            isInceptionToDate,
          },
        })
          .then(
            (response: {
              data: { data: { uploadStatement: UploadStatementPayload } };
            }) => {
              const payload = response.data.data.uploadStatement;
              const failure = payload.errors?.[0];

              if (failure || !payload.upload) {
                setUploadError(
                  failure
                    ? toValidationError(failure.code, failure.details)
                    : ({
                        code: "parse_failed",
                        message: "The upload did not go through. Try again.",
                      } as ValidationError),
                );
                return;
              }

              const formatted = formatUploadPayload(payload.upload);
              setListQuery((current) => ({
                ...FIRST_UPLOADS_PAGE,
                pageSize: current.pageSize,
              }));
              toast.push({
                kind: "success",
                title: `${formatted.id} uploaded`,
                desc: "We’re checking the file. You can leave this page.",
              });
            },
          )
          .catch((exception: unknown) => {
            Sentry.captureException(exception);
            toast.push({
              kind: "warn",
              title: "We couldn’t confirm the upload",
              desc: "Check the list below before trying again.",
            });
            loadRecordsRef.current();
          })
          .finally(() => setUploading(false));
      };
      reader.onerror = (): void => {
        setUploadError({
          code: "parse_failed",
          message:
            "We couldn’t read that file from your computer. Pick it again and retry.",
        } as ValidationError);
        setUploading(false);
      };

      reader.readAsDataURL(file);
    },
    [toast],
  );

  const handleReplace = useCallback(
    (rec: FormattedUpload, file: File) => {
      const reader = new FileReader();
      reader.onload = (): void => {
        const dataUri = String(reader.result || "");
        const extension = (file.name.split(".").pop() || "xlsx").toLowerCase();
        request({
          query: UPLOAD_FILE,
          variables: {
            data: dataUri,
            extension,
            source: "phoenix",
            fileType: "bulk_upload_payment_statement",
            skipJobProcess: true,
          },
        })
          .then(
            (response: {
              data: {
                data: {
                  uploadFile: {
                    fileUploadId: string | null;
                    errors: Array<{ property: string; description: string }>;
                  };
                };
              };
            }) => {
              const payload = response.data.data.uploadFile;
              if (!payload.fileUploadId || payload.errors?.length) {
                toast.push({
                  kind: "warn",
                  title: "Upload failed",
                  desc:
                    payload.errors?.[0]?.description ||
                    "Could not link FileUpload",
                });
                return null;
              }
              return request({
                query: REPLACE_STATEMENT_UPLOAD_FILE,
                variables: { id: rec.dbId, fileUploadId: payload.fileUploadId },
              });
            },
          )
          .then((replaceResponse) => {
            if (!replaceResponse) return;
            const replaced = (
              replaceResponse as {
                data: {
                  data: {
                    replaceStatementUploadFile: {
                      upload: unknown;
                      errors: Array<{ code: string }>;
                    };
                  };
                };
              }
            ).data.data.replaceStatementUploadFile;
            if (replaced.errors?.length || !replaced.upload) {
              toast.push({
                kind: "warn",
                title: "Replace rejected",
                desc: replaced.errors?.[0]?.code || "Unknown",
              });
              return;
            }
            toast.push({
              kind: "success",
              title: "File replaced",
              desc: `${file.name} will reconcile as planned.`,
            });
            loadRecords();
          })
          .catch((error: unknown) => {
            Sentry.captureException(error);
            toast.push({
              kind: "warn",
              title: "Replace failed",
              desc: "Network error.",
            });
          });
      };
      reader.readAsDataURL(file);
    },
    [toast, loadRecords],
  );

  const sendReschedule = useCallback(
    (rec: FormattedUpload, newDate: string, reason: string) => {
      // newDate is an ISO timestamp produced by the ScheduledManager picker —
      // it can flow to the mutation as-is. We still validate so a malformed
      // string from any other path doesn't reach the server.
      const parsed = new Date(newDate);
      if (Number.isNaN(parsed.getTime())) {
        toast.push({
          kind: "warn",
          title: "Reschedule failed",
          desc: `Couldn't read the new date (${newDate}).`,
        });
        return;
      }
      const isoDate = parsed.toISOString();
      request({
        query: RESCHEDULE_STATEMENT_UPLOAD,
        variables: { id: rec.dbId, scheduledFor: isoDate, reason },
      })
        .then(
          (response: {
            data: {
              data: {
                rescheduleStatementUpload: {
                  upload: unknown;
                  errors: Array<{ property: string; description: string }>;
                };
              };
            };
          }) => {
            const result = response.data.data.rescheduleStatementUpload;
            if (result.errors?.length || !result.upload) {
              toast.push({
                kind: "warn",
                title: "Reschedule failed",
                desc: result.errors?.[0]?.description || "Unknown",
              });
              return;
            }
            toast.push({
              kind: "success",
              title: "New date set",
              desc: `${rec.filename} will now reconcile on ${
                newDate.split(" · ")[0]
              }.`,
            });
            loadRecords();
          },
        )
        .catch((error: unknown) => {
          Sentry.captureException(error);
          toast.push({
            kind: "warn",
            title: "Reschedule failed",
            desc: "Network error.",
          });
        });
    },
    [toast, loadRecords],
  );

  const handleReschedule = useCallback(
    (rec: FormattedUpload, newDate: string) => {
      askForReason({
        title: "Change the run date",
        description: `Tell us why ${
          rec.filename
        } should reconcile on ${formatFriendly(newDate)} instead.`,
        okText: "Move date",
        onConfirm: (reason) => {
          setReasonPrompt(null);
          sendReschedule(rec, newDate, reason);
        },
      });
    },
    [toast, askForReason, sendReschedule],
  );

  const sendCancel = useCallback(
    (rec: FormattedUpload, reason: string) => {
      request({
        query: CANCEL_STATEMENT_UPLOAD,
        variables: { id: rec.dbId, reason },
      })
        .then(
          (response: {
            data: {
              data: {
                cancelStatementUpload: {
                  errors: Array<{ property: string; description: string }>;
                };
              };
            };
          }) => {
            const errors =
              response.data.data.cancelStatementUpload?.errors ?? [];
            if (errors.length) {
              toast.push({
                kind: "warn",
                title: "Cancel failed",
                desc: errors[0].description,
              });
              return;
            }
            setDrawerOpen(false);
            toast.push({
              kind: "info",
              title:
                rec.status === "failed"
                  ? "Upload abandoned"
                  : "Reconciliation canceled",
              desc: `${rec.filename} (${rec.id}) has been canceled.`,
            });
            loadRecords();
          },
        )
        .catch((error: unknown) => {
          Sentry.captureException(error);
          toast.push({
            kind: "warn",
            title: "Cancel failed",
            desc: "Network error.",
          });
        });
    },
    [toast, loadRecords],
  );

  const handleCancelScheduled = useCallback(
    (rec: FormattedUpload) => {
      const checking = rec.status === "parsing";
      askForReason({
        title: checking ? "Cancel this upload" : "Cancel this reconciliation",
        description: `Tell us why ${rec.filename} (${rec.id}) shouldn't go ahead. Anyone reviewing the audit trail will see this.`,
        okText: checking ? "Cancel upload" : "Cancel reconciliation",
        okDanger: true,
        onConfirm: (reason) => {
          setReasonPrompt(null);
          sendCancel(rec, reason);
        },
      });
    },
    [askForReason, sendCancel],
  );

  const sendExpedite = useCallback(
    (rec: FormattedUpload, reason: string) => {
      request({
        query: EXPEDITE_STATEMENT_UPLOAD,
        variables: { id: rec.dbId, reason },
      })
        .then(
          (response: {
            data: {
              data: {
                expediteStatementUpload: {
                  errors: Array<{ property: string; description: string }>;
                };
              };
            };
          }) => {
            const errors =
              response.data.data.expediteStatementUpload?.errors ?? [];
            if (errors.length) {
              toast.push({
                kind: "warn",
                title: "Couldn't start it now",
                desc: errors[0].description,
              });
              return;
            }
            toast.push({
              kind: "info",
              title: "Starting reconciliation now",
              desc: `${rec.filename} has been queued for immediate processing.`,
            });
            loadRecords();
          },
        )
        .catch((error: unknown) => {
          Sentry.captureException(error);
          toast.push({
            kind: "warn",
            title: "Couldn't start it now",
            desc: "Network error.",
          });
        });
    },
    [toast, loadRecords],
  );

  const handleExpedite = useCallback(
    (rec: FormattedUpload) => {
      askForReason({
        title: "Start this reconciliation now",
        description: `This skips the scheduled wait and queues ${rec.filename} (${rec.id}) for immediate processing. Tell us why — the audit trail will keep this on record.`,
        okText: "Start now",
        onConfirm: (reason) => {
          setReasonPrompt(null);
          sendExpedite(rec, reason);
        },
      });
    },
    [askForReason, sendExpedite],
  );

  const handleRetry = useCallback(
    (record: FormattedUpload) => {
      request({ query: RETRY_STATEMENT_UPLOAD, variables: { id: record.dbId } })
        .then(
          (response: {
            data: {
              data: {
                retryStatementUpload: {
                  errors: Array<{ property: string; description: string }>;
                };
              };
            };
          }) => {
            const errors =
              response.data.data.retryStatementUpload?.errors ?? [];
            if (errors.length) {
              toast.push({
                kind: "warn",
                title: "Retry failed",
                desc: errors[0].description,
              });
              return;
            }
            toast.push({
              kind: "info",
              title: "Re-queued",
              desc: `${record.filename} is back in the queue.`,
            });
            loadRecords();
          },
        )
        .catch((error: unknown) => {
          Sentry.captureException(error);
          toast.push({
            kind: "warn",
            title: "Retry failed",
            desc: "Network error.",
          });
        });
    },
    [toast, loadRecords],
  );

  const handleDownload = useCallback(
    (record: FormattedUpload) => {
      toast.push({
        kind: "success",
        title: "Preparing download",
        desc: `Bundling ${record.id} (original + reconciled) as a .zip.`,
      });
      request({
        query: DOWNLOAD_STATEMENT_UPLOAD_BUNDLE,
        variables: { id: record.dbId },
      })
        .then(
          (response: {
            data: {
              data: {
                downloadStatementUploadBundle: {
                  zipBase64: string | null;
                  filename: string | null;
                  errors: Array<{ description: string }>;
                };
              };
            };
          }) => {
            const payload = response?.data?.data?.downloadStatementUploadBundle;
            if (!payload?.zipBase64) {
              const description = (payload?.errors || [])
                .map((error) => error.description)
                .join("; ");
              toast.push({
                kind: "error",
                title: "Download failed",
                desc: description || "The bundle couldn't be generated.",
              });
              return;
            }
            const binary = atob(payload.zipBase64);
            const bytes = new Uint8Array(binary.length);
            for (let index = 0; index < binary.length; index++)
              bytes[index] = binary.charCodeAt(index);
            const blob = new Blob([bytes], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download =
              payload.filename || `${record.id}-reconciliation.zip`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          },
        )
        .catch((error: unknown) => {
          Sentry.captureException(error);
          toast.push({
            kind: "error",
            title: "Download failed",
            desc: "Something went wrong while preparing the bundle.",
          });
        });
    },
    [toast],
  );

  const handleReverseRequest = useCallback(
    (rec: FormattedUpload, reason: string) => {
      request({
        query: REVERSE_STATEMENT_UPLOAD,
        variables: { id: rec.dbId, reason },
      })
        .then(
          (response: {
            data: {
              data: {
                reverseStatementUpload: {
                  errors: Array<{ property: string; description: string }>;
                };
              };
            };
          }) => {
            const errors =
              response.data.data.reverseStatementUpload?.errors ?? [];
            if (errors.length) {
              toast.push({
                kind: "warn",
                title: "Reverse failed",
                desc: errors[0].description,
              });
              return;
            }
            toast.push({
              kind: rec.status === "processing" ? "warn" : "warn",
              title:
                rec.status === "processing"
                  ? "Reversal queued"
                  : "Reversing reconciliation",
              desc:
                rec.status === "processing"
                  ? `${rec.filename} will reverse once the current run finishes.`
                  : `Marking the recon entries from ${rec.filename} as reversed.`,
            });
            loadRecords();
          },
        )
        .catch((error: unknown) => {
          Sentry.captureException(error);
          toast.push({
            kind: "warn",
            title: "Reverse failed",
            desc: "Network error.",
          });
        });
    },
    [toast, loadRecords],
  );

  const handleRefresh = useCallback(() => {
    loadRecords();
    toast.push({
      kind: "info",
      title: "Refreshing…",
      desc: "Fetching the latest uploads.",
    });
  }, [loadRecords, toast]);

  const canEdit = tweaks.role === "editor";

  return (
    <div className="ps-page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div className="page-tabs" role="tablist" style={{ marginBottom: 0 }}>
          <button
            className={`page-tab ${tab === "overview" ? "active" : ""}`}
            onClick={() => setTabAndUrl("overview")}
          >
            <Icon name="chart" size={14} strokeWidth={2.2} /> Payment
            reconciliation
          </button>
          <button
            className={`page-tab ${tab === "uploads" ? "active" : ""}`}
            onClick={() => setTabAndUrl("uploads")}
          >
            <Icon name="upload" size={14} strokeWidth={2.2} /> Uploads
          </button>
        </div>
        <button
          className="btn btn-primary"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 36,
          }}
          onClick={() => setNotificationsOpen(true)}
        >
          <NotificationOutlined style={{ fontSize: 14 }} />
          <span>Notifications</span>
        </button>
      </div>
      {tab === "overview" && (
        <OverviewTab role={tweaks.role} onOpenRow={setRowDrawerRow} />
      )}
      {tab === "uploads" && (
        <UploadsTab
          records={records}
          totalUploads={totalUploads}
          listQuery={listQuery}
          onListQueryChange={setListQuery}
          role={tweaks.role}
          onUpload={handleUpload}
          uploading={uploading}
          uploadError={uploadError}
          onClearUploadError={() => setUploadError(null)}
          onOpen={openDrawer}
          onOpenById={openDrawerById}
          onRetry={handleRetry}
          onDownload={handleDownload}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />
      )}

      <AuditDrawer
        record={
          drawerRec
            ? { ...drawerRec, issues: drawerIssues ?? drawerRec.issues }
            : null
        }
        open={drawerOpen}
        onClose={closeDrawer}
        onRetry={(record) => {
          handleRetry(record);
          closeDrawer();
        }}
        onDownload={handleDownload}
        onReplace={handleReplace}
        onReschedule={handleReschedule}
        onCancelScheduled={handleCancelScheduled}
        onUploadCorrected={() => {
          setDrawerOpen(false);
          document
            .querySelector(".ps-page .card")
            ?.scrollIntoView({ behavior: "smooth" });
        }}
        onReverseRequest={handleReverseRequest}
        onExpedite={handleExpedite}
        onOpenIssue={(row) => {
          closeDrawer();
          setRowDrawerRow(row);
        }}
        canEdit={canEdit}
        canReschedule={canReschedule}
        canReverse={canReverse}
        canExpedite={canExpedite}
        events={drawerEvents}
      />
      {rowDrawerRow && (
        <RowDetailDrawer
          row={rowDrawerRow}
          onClose={() => setRowDrawerRow(null)}
        />
      )}
      <ReasonModal
        open={!!reasonPrompt}
        title={reasonPrompt?.title || ""}
        description={reasonPrompt?.description}
        okText={reasonPrompt?.okText}
        okDanger={reasonPrompt?.okDanger}
        onConfirm={(reason) => reasonPrompt?.onConfirm(reason)}
        onCancel={() => setReasonPrompt(null)}
      />
      <Drawer
        title="Reconciliation Notifications"
        visible={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        width={notificationsMaximized ? "100vw" : 1000}
        extra={
          <button
            onClick={() => setNotificationsMaximized(!notificationsMaximized)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 8,
              borderRadius: 4,
              border: "none",
              background: "none",
              color: "var(--muted)",
              cursor: "pointer",
            }}
            title={
              notificationsMaximized
                ? "Restore window size"
                : "Maximize window size"
            }
          >
            <Icon
              name={notificationsMaximized ? "minimize" : "maximize"}
              size={16}
              strokeWidth={2}
            />
          </button>
        }
      >
        <NotificationsView onClose={() => setNotificationsOpen(false)} />
      </Drawer>
    </div>
  );
};

class ProductStatements extends React.Component<Props> {
  public render(): JSX.Element {
    return (
      <ToastProvider>
        <ProductStatementsInner {...this.props} />
      </ToastProvider>
    );
  }
}

export default ProductStatements;
